import { FetchArgs, FetchBaseQueryArgs } from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import { BaseQueryApi, createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Toast } from "react-native-toast-notifications";
import { Platform } from "react-native";
import * as SecureStore from "expo-secure-store";
import { newRidgeState } from "react-ridge-state";

export const apiRunningState = newRidgeState<boolean>(false);

/**
 * Retrieves the Clerk authentication token.
 * - On web: Uses `fetch` to retrieve the session token from Clerk.
 * - On mobile: Retrieves the token from SecureStore.
 */
async function getClerkJwt() {
	let token = null;

	try {
		if (Platform.OS === "web") {
			// Ensure Clerk is initialized
			if (window.Clerk && window.Clerk.client && window.Clerk.client.sessions && window.Clerk.client.sessions.length > 0) {
				// @ts-ignore
				token = await window.Clerk.client.sessions[0].getToken();
			}
		} else {
			// Retrieve token from SecureStore on mobile
			token = await SecureStore.getItemAsync("__clerk_client_jwt");
		}
	} catch (error) {
		console.error("Error retrieving Clerk JWT:", error);
	}

	return token ? `Bearer ${token}` : null;
}

/**
 * Serializes query parameters properly for API requests.
 */
const paramsSerializer = (params: Record<string, any>): string => {
	const searchParams = new URLSearchParams();

	for (const key in params) {
		const value = params[key];

		if (value === undefined) {
			continue; // Skip undefined values
		}

		if (Array.isArray(value)) {
			value.forEach((item) => searchParams.append(`${key}`, item));
		} else {
			searchParams.append(key, value);
		}
	}

	return searchParams.toString();
};

function uuidv4() {
	return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16));
}

const getOrCreateAnonymousSession = () => {
	const key = "anon_session";
	let sessionToken = localStorage.getItem(key);

	if (!sessionToken) {
		sessionToken = uuidv4();
		localStorage.setItem(key, sessionToken);
	}

	return sessionToken;
};

/**
 * Custom baseQuery function that injects Clerk authentication token into API requests.
 */
const baseQuery = (baseQueryOptions: FetchBaseQueryArgs | undefined) => async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: {}) => {
	// show loading indicator
	var showLoading = true;
	if (api.endpoint === "getNotification") {
		showLoading = false;
	}

	apiRunningState.set(showLoading);

	// Initialize headers
	const headers = new Headers({ "Content-Type": "application/json" });
	const token = await getClerkJwt();
	const anonSessionToken = getOrCreateAnonymousSession();
	if (token) {
		headers.set("Authorization", token);
		headers.set("X-Session", anonSessionToken);
	} else {
		headers.set("X-Session", anonSessionToken);
	}

	// Attach headers to the request
	if (typeof args === "string") {
		args = { url: args, headers };
	} else {
		args.headers = headers;
	}

	const result = await fetchBaseQuery(baseQueryOptions)(args, api, extraOptions);
	apiRunningState.set(false);

	// Handle file responses (Excel, PDF, etc.)
	if (result.data && typeof result.data === 'string' && result.data.startsWith('PK')) {
		// This is likely an Excel file or other binary file
		console.log("File response detected - Excel or binary file");
		return result;
	}
	
	if (result.data instanceof Blob) {
		console.log("Blob response detected");
		return result;
	}
	
	// Handle the new file response structure
	if (result.data && typeof result.data === 'object' && '__isFileResponse' in result.data && result.data.__isFileResponse) {
		console.log("File response detected - new structure");
		// Don't cache file responses to avoid Redux serialization issues
		return {
			...result,
			meta: {
				...result.meta,
				requestId: undefined, // Prevent caching
			}
		};
	}

	// Handle API errors
	if (result.error && result.error.status !== 404 && showLoading) {
		let message = result.error?.data?.message || result.error?.title || "An unknown error occurred";

		if (message.includes("Violation of PRIMARY KEY")) {
			return result;
		}

		if (message)
			Toast.show(message, {
				type: "danger",
				textStyle: { fontSize: 20 },
				placement: "bottom",
			});
	}

	return result;
};

/**
 * Initializes an empty API service that will have endpoints injected later.
 */
export const emptySplitApi = createApi({
	baseQuery: baseQuery({
		baseUrl: process.env.EXPO_PUBLIC_ABCO_API_URL,
		paramsSerializer,
		responseHandler: async (response: Response) => {
			// Check if response is a file download
			const contentType = response.headers.get('content-type');
			const contentDisposition = response.headers.get('content-disposition');
			
			if (contentType && (
				contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
				contentType.includes('application/vnd.ms-excel') ||
				contentType.includes('application/pdf') ||
				contentType.includes('application/octet-stream') ||
				contentDisposition?.includes('attachment')
			)) {
				// For file downloads, return a serializable object instead of blob
				const blob = await response.blob();
				// Store blob in a global variable to avoid Redux serialization issues
				const fileId = `file_${Date.now()}_${Math.random()}`;
				(window as any).__fileDownloads = (window as any).__fileDownloads || {};
				(window as any).__fileDownloads[fileId] = {
					blob,
					contentType,
					contentDisposition,
					size: blob.size
				};
				
				return {
					__isFileResponse: true,
					fileId: fileId,
					contentType: contentType,
					contentDisposition: contentDisposition,
					size: blob.size
				};
			}
			
			// Default JSON parsing for other responses
			return response.json();
		},
	}),
	endpoints: () => ({}),
	// Allow non-serializable values to prevent Redux warnings for file responses
	keepUnusedDataFor: 0,
	reducerPath: 'api',
});
